import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  SubscriptionPlan,
  OrganizationSubscription,
  PlanFeature,
  PaymentHistory,
  BillingInvoice,
  IBillingSummary,
  GracePeriodStatus,
  TrialStatus,
  calculateTrialDisplayDaysRemaining,
} from '../../services/subscription.service';
import type { RootState } from '../store';

// Import actions from separated action file
import {
  fetchSubscriptionPlans,
  fetchPlanDetails,
  fetchCurrentSubscription,
  fetchOrganizationSubscription,
  createSubscription,
  updateSubscription,
  cancelSubscription,
  fetchSubscriptionFeatures,
  checkFeature,
  initiatePayment,
  fetchPaymentHistory,
  fetchPaymentById,
  fetchInvoiceByPaymentId,
  fetchBillingInvoiceById,
  convertTrialToPaid,
  fetchMyBillingHistory,
  fetchMyBillingSummary,
  fetchMyGracePeriodStatus,
  fetchMyTrialStatus,
  fetchMySubscriptionFeatures,
} from '../action/subscription.action';

// Re-export actions for backward compatibility
export {
  fetchSubscriptionPlans,
  fetchPlanDetails,
  fetchCurrentSubscription,
  fetchOrganizationSubscription,
  createSubscription,
  updateSubscription,
  cancelSubscription,
  fetchSubscriptionFeatures,
  checkFeature,
  initiatePayment,
  fetchPaymentHistory,
  fetchPaymentById,
  fetchInvoiceByPaymentId,
  fetchBillingInvoiceById,
  convertTrialToPaid,
  fetchMyBillingHistory,
  fetchMyBillingSummary,
  fetchMyGracePeriodStatus,
  fetchMyTrialStatus,
  fetchMySubscriptionFeatures,
} from '../action/subscription.action';

// State interface
interface ISubscriptionState {
  isLoading: boolean;
  isCheckingExpiry: boolean;
  gracePeriodLoading: boolean;
  trialLoading: boolean;
  error: string | null;
  // Plans
  plans: SubscriptionPlan[];
  selectedPlan: SubscriptionPlan | null;
  // Current subscription
  currentSubscription: OrganizationSubscription | null;
  // Features
  features: PlanFeature[];
  featuresByCategory: Record<string, PlanFeature[]>;
  featureCheck: { hasFeature: boolean; feature?: PlanFeature } | null;
  // Payment
  paymentHistory: PaymentHistory[];
  selectedPayment: PaymentHistory | null;
  invoice: any | null;
  // Billing
  billingInvoices: BillingInvoice[];
  selectedBillingInvoice: BillingInvoice | null;
  billingSummary: IBillingSummary | null;
  gracePeriodStatus: GracePeriodStatus | null;
  trialStatus: TrialStatus | null;
}

// Default state
const initialState: ISubscriptionState = {
  isLoading: false,
  isCheckingExpiry: false,
  gracePeriodLoading: false,
  trialLoading: false,
  error: null,
  plans: [],
  selectedPlan: null,
  currentSubscription: null,
  features: [],
  featuresByCategory: {},
  featureCheck: null,
  paymentHistory: [],
  selectedPayment: null,
  invoice: null,
  billingInvoices: [],
  selectedBillingInvoice: null,
  billingSummary: null,
  gracePeriodStatus: null,
  trialStatus: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    resetSubscriptionState: () => initialState,
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedPlan: (state) => {
      state.selectedPlan = null;
    },
    clearSelectedPayment: (state) => {
      state.selectedPayment = null;
      state.invoice = null;
    },
  },
  extraReducers: (builder) => {
    // fetchSubscriptionPlans
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plans = action.payload.data;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || i18n.t('toast.failed_to_fetch_plans', 'Failed to fetch plans');
      });

    // fetchPlanDetails
    builder
      .addCase(fetchPlanDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlanDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPlan = action.payload.data;
      })
      .addCase(fetchPlanDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_plan_details', 'Failed to fetch plan details');
      });

    // fetchCurrentSubscription — uses dedicated isCheckingExpiry flag (NOT shared isLoading)
    // so SubscriptionGuard redirect is never blocked by other in-flight fetches.
    // Also derives trialStatus from the response to eliminate a redundant /me/subscription call.
    builder
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.isCheckingExpiry = true;
        state.error = null;
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.isCheckingExpiry = false;
        const sub = action.payload.data;
        state.currentSubscription = sub;

        // Derive trialStatus from subscription data — avoids a separate /me/subscription call
        if (!sub) {
          // leave trialStatus unchanged
        } else if (!sub.is_trial) {
          state.trialStatus = {
            isTrial: false,
            trialDaysRemaining: null,
            trialEndsAt: null,
            severity: null,
          };
        } else {
          const rawEndDate = (sub as any).trialEndDate || (sub as any).trial_end_date || null;
          if (!rawEndDate) {
            state.trialStatus = {
              isTrial: true,
              trialDaysRemaining: null,
              trialEndsAt: null,
              severity: 'info',
            };
          } else {
            const daysRemaining = calculateTrialDisplayDaysRemaining(rawEndDate as string) ?? 0;
            let severity: 'info' | 'warning' | 'critical' = 'info';
            if (daysRemaining <= 2) severity = 'critical';
            else if (daysRemaining <= 5) severity = 'warning';
            state.trialStatus = {
              isTrial: true,
              trialDaysRemaining: Math.max(0, daysRemaining),
              trialEndsAt: rawEndDate,
              severity,
            };
          }
        }
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.isCheckingExpiry = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_subscription', 'Failed to fetch subscription');
        // Clear stale subscription data so guard logic never evaluates a previous
        // user's status (e.g. a 403 for tenant users must not leave old data in place).
        state.currentSubscription = null;
      });

    // fetchOrganizationSubscription
    builder
      .addCase(fetchOrganizationSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload.data;
      })
      .addCase(fetchOrganizationSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_organization_subscription',
            'Failed to fetch organization subscription',
          );
      });

    // createSubscription
    builder
      .addCase(createSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload.data;
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_create_subscription', 'Failed to create subscription');
      });

    // updateSubscription
    builder
      .addCase(updateSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload.data;
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_update_subscription', 'Failed to update subscription');
      });

    // cancelSubscription
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload.data;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_cancel_subscription', 'Failed to cancel subscription');
      });

    // fetchSubscriptionFeatures
    builder
      .addCase(fetchSubscriptionFeatures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionFeatures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features = action.payload.data.features;
        state.featuresByCategory = action.payload.data.featuresByCategory;
      })
      .addCase(fetchSubscriptionFeatures.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_features', 'Failed to fetch features');
      });

    // checkFeature
    builder
      .addCase(checkFeature.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkFeature.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featureCheck = action.payload.data;
      })
      .addCase(checkFeature.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_check_feature', 'Failed to check feature');
      });

    // initiatePayment
    builder
      .addCase(initiatePayment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initiatePayment.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(initiatePayment.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_initiate_payment', 'Failed to initiate payment');
      });

    // fetchPaymentHistory
    builder
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentHistory = action.payload.data;
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_payment_history', 'Failed to fetch payment history');
      });

    // fetchPaymentById
    builder
      .addCase(fetchPaymentById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPayment = action.payload.data;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_payment', 'Failed to fetch payment');
      });

    // fetchInvoiceByPaymentId
    builder
      .addCase(fetchInvoiceByPaymentId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceByPaymentId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invoice = action.payload.data;
      })
      .addCase(fetchInvoiceByPaymentId.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_invoice', 'Failed to fetch invoice');
      });

    // fetchBillingInvoiceById
    builder
      .addCase(fetchBillingInvoiceById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBillingInvoiceById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedBillingInvoice = action.payload.data;
      })
      .addCase(fetchBillingInvoiceById.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_billing_invoice', 'Failed to fetch billing invoice');
      });

    // convertTrialToPaid
    builder
      .addCase(convertTrialToPaid.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(convertTrialToPaid.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload.data;
      })
      .addCase(convertTrialToPaid.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_convert_trial', 'Failed to convert trial');
      });

    // fetchMyBillingHistory
    builder
      .addCase(fetchMyBillingHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyBillingHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.billingInvoices = action.payload.data;
      })
      .addCase(fetchMyBillingHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_billing_history', 'Failed to fetch billing history');
      });

    // fetchMyBillingSummary
    builder
      .addCase(fetchMyBillingSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyBillingSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.billingSummary = action.payload;
      })
      .addCase(fetchMyBillingSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_billing_summary', 'Failed to fetch billing summary');
      });

    // fetchMyGracePeriodStatus
    builder
      .addCase(fetchMyGracePeriodStatus.pending, (state) => {
        state.gracePeriodLoading = true;
        state.error = null;
      })
      .addCase(fetchMyGracePeriodStatus.fulfilled, (state, action) => {
        state.gracePeriodLoading = false;
        state.gracePeriodStatus = action.payload;
      })
      .addCase(fetchMyGracePeriodStatus.rejected, (state, action) => {
        state.gracePeriodLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_grace_period_status',
            'Failed to fetch grace period status',
          );
      });

    // fetchMyTrialStatus
    builder
      .addCase(fetchMyTrialStatus.pending, (state) => {
        state.trialLoading = true;
        state.error = null;
      })
      .addCase(fetchMyTrialStatus.fulfilled, (state, action) => {
        state.trialLoading = false;
        state.trialStatus = action.payload;
      })
      .addCase(fetchMyTrialStatus.rejected, (state, action) => {
        state.trialLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_trial_status', 'Failed to fetch trial status');
      });

    // fetchMySubscriptionFeatures
    builder
      .addCase(fetchMySubscriptionFeatures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMySubscriptionFeatures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features = action.payload.data.features;
        state.featuresByCategory = action.payload.data.featuresByCategory;
      })
      .addCase(fetchMySubscriptionFeatures.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_subscription_features',
            'Failed to fetch subscription features',
          );
      });
  },
});

export const { resetSubscriptionState, clearError, clearSelectedPlan, clearSelectedPayment } =
  subscriptionSlice.actions;

// Selectors
export const selectSubscriptionState = (state: RootState) => state.subscription;
export const selectSubscriptionLoading = (state: RootState) => state.subscription.isLoading;
export const selectCheckingExpiry = (state: RootState) => state.subscription.isCheckingExpiry;
export const selectGracePeriodLoading = (state: RootState) => state.subscription.gracePeriodLoading;
export const selectTrialLoading = (state: RootState) => state.subscription.trialLoading;
export const selectSubscriptionError = (state: RootState) => state.subscription.error;
export const selectGracePeriodStatus = (state: RootState) => state.subscription.gracePeriodStatus;
export const selectTrialStatus = (state: RootState) => state.subscription.trialStatus;
export const selectBillingSummary = (state: RootState) => state.subscription.billingSummary;
export const selectCurrentSubscription = (state: RootState) =>
  state.subscription.currentSubscription;
export const selectSubscriptionFeatures = (state: RootState) => state.subscription.features;
export const selectSubscriptionPlans = (state: RootState) => state.subscription.plans;

export default subscriptionSlice.reducer;
